import {cdnImage} from '@/util/cdnImage';
import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
const checkDark = cdnImage('white-label-exchange-development-new/pricing-check-dark.svg');
const checkLight = cdnImage('white-label-exchange-development-new/pricing-check-light.svg');
const trustNoLock = cdnImage('white-label-exchange-development-new/pricing-trust-no-lock.svg');
const trustSourceCode = cdnImage('white-label-exchange-development-new/pricing-trust-source-code.svg');
const trustSupport = cdnImage('white-label-exchange-development-new/pricing-trust-support.svg');
const trustOnboarding = cdnImage('white-label-exchange-development-new/pricing-trust-onboarding.svg');
import DisplayImage from '@/components/Ui-components/display-image';
import {borderRadius, colors, fontWeight, shadows} from '@/styles/tokens';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PricingTier {
    tier: string;
    price: string;
    bestFor: string;
    features: string[];
    buttonText: string;
    featured?: boolean;
}

interface WhiteLabelPricingProps {
    heading?: string;
    subheading?: string;
    tiers: PricingTier[];
    openForm?: () => void;
}

// ─── sx constants ─────────────────────────────────────────────────────────────

const lightCardSx = {
    bgcolor: colors.background.default,
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: borderRadius['3xl'],
    p: '41px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
} as const;

const darkCardSx = {
    background: 'linear-gradient(to left, #244486, #252e4f)',
    border: '1px solid rgba(37,99,235,0.5)',
    borderRadius: borderRadius['3xl'],
    px: '41px',
    py: '57px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
    boxShadow: shadows.sm
} as const;

const tierLabelLightSx = {
    fontWeight: fontWeight.semibold,
    fontSize: 14,
    color: '#7b8590',
    letterSpacing: '0.7px',
    mb: 1.5,
    textTransform: 'uppercase'
} as const;

const tierLabelDarkSx = {
    ...tierLabelLightSx,
    color: colors.text.inverse
} as const;

const priceLightSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: 28, sm: 36},
    color: '#0f1724',
    lineHeight: '54px'
} as const;

const priceDarkSx = {
    ...priceLightSx,
    color: colors.text.inverse
} as const;

const bestForLightSx = {
    fontSize: 14,
    color: colors.primary.main,
    lineHeight: '21px',
    minHeight: 42,
    mb: 3
} as const;

const bestForDarkSx = {
    ...bestForLightSx,
    color: colors.accent.orange
} as const;

const featureTextLightSx = {fontSize: 15, color: '#000', lineHeight: '22.5px'} as const;
const featureTextDarkSx = {fontSize: 15, color: '#fff', lineHeight: '22.5px'} as const;

const lightBtnSx = {
    bgcolor: colors.primary.main,
    color: '#fff',
    borderRadius: borderRadius.lg,
    py: '17px',
    fontWeight: fontWeight.bold,
    fontSize: 16,
    textTransform: 'none',
    mt: 'auto',
    '&:hover': {bgcolor: '#1a3366'}
} as const;

const orangeBtnSx = {
    bgcolor: colors.accent.orange,
    color: '#fff',
    borderRadius: borderRadius.lg,
    py: '16px',
    fontWeight: fontWeight.bold,
    fontSize: 16,
    textTransform: 'none',
    mt: 'auto',
    boxShadow: shadows.lg,
    '&:hover': {bgcolor: '#e5832a'}
} as const;

const popularBadgeSx = {
    position: 'absolute',
    top: -15,
    left: '50%',
    transform: 'translateX(-50%)',
    bgcolor: colors.accent.orange,
    borderRadius: borderRadius.lg,
    px: 2,
    py: '6px',
    whiteSpace: 'nowrap'
} as const;

// ─── Trust strip ──────────────────────────────────────────────────────────────

const trustStripSx = {
    bgcolor: colors.background.default,
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: borderRadius.xl,
    p: '33px',
    boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
    display: 'grid',
    gridTemplateColumns: {xs: '1fr 1fr', md: 'repeat(4, 1fr)'},
    gap: 3,
    mt: 4
} as const;

const iconWrapSx = {
    width: 48,
    height: 48,
    bgcolor: '#f1f5f9',
    borderRadius: borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mb: 2,
    flexShrink: 0,
    transition: 'background-color 0.25s ease'
} as const;

const trustItemContentSx = {
    borderRadius: borderRadius.lg,
    p: 1.5,
    transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
    '&:hover': {
        transform: 'translateY(-5px)',
        backgroundColor: 'rgba(36,68,134,0.04)',
        boxShadow: '0 8px 24px rgba(36,68,134,0.1)',
        '& > div:first-of-type': {
            backgroundColor: '#dbeafe'
        }
    }
} as const;

const trustItems = [
    {icon: trustNoLock, title: 'No Lock-in Contracts', body: 'Cancel anytime, no long-term commitments'},
    {icon: trustSourceCode, title: 'Full Source Code Ownership', body: 'Available on Enterprise plans'},
    {icon: trustSupport, title: '30-Day Free Support', body: 'Post-launch support included on all tiers'},
    {icon: trustOnboarding, title: 'White-glove Onboarding', body: 'Dedicated setup and guidance'}
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const FeatureItem = ({text, dark}: {text: string; dark?: boolean}) => (
    <Box display="flex" gap="12px" alignItems="flex-start">
        <Box flexShrink={0} width={18} height={18} position="relative" mt="3px">
            <DisplayImage src={dark ? checkLight : checkDark} alt="" width={18} height={18} style={{width: '18px', height: '18px'}} />
        </Box>
        <Typography className="font-regular-text" sx={dark ? featureTextDarkSx : featureTextLightSx}>
            {text}
        </Typography>
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

const WhiteLabelPricing = ({
    heading = 'Transparent Pricing for Every Stage of Your Exchange Business',
    subheading = 'From MVPs to institutional platforms, we have a deployment model that fits your budget and ambitions. No hidden fees. No lock-in contracts. Source code ownership available on Enterprise tier.',
    tiers,
    openForm
}: WhiteLabelPricingProps) => {
    return (
        <Box sx={{bgcolor: colors.background.subtle}} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
            <MUIContainer>
                <Box textAlign="center" maxWidth={1093} margin="0 auto" mb={{xs: 5, md: 7}}>
                    <Typography variant="h2" component="h2" mb={3} fontSize={{xs: 32, sm: 52}} fontWeight={800} className="font-headers">
                        {heading}
                    </Typography>
                    <Typography variant="body1" className="font-regular-text" sx={{fontSize: 16, color: '#000', lineHeight: '26px'}}>
                        {subheading}
                    </Typography>
                </Box>

                <Grid container spacing={3} alignItems="center">
                    {tiers.map((tier, idx) => (
                        <Grid item xs={12} md={4} key={idx} sx={{display: 'flex'}}>
                            {tier.featured ? (
                                <Box sx={{...darkCardSx, width: '100%'}}>
                                    {/* Most Popular badge */}
                                    <Box sx={popularBadgeSx}>
                                        <Typography
                                            className="font-regular-text"
                                            sx={{fontWeight: fontWeight.bold, fontSize: 12, color: '#fff', letterSpacing: '0.6px'}}
                                        >
                                            ⭐ MOST POPULAR
                                        </Typography>
                                    </Box>

                                    <Typography className="font-headers" sx={tierLabelDarkSx}>
                                        {tier.tier}
                                    </Typography>
                                    <Typography className="font-headers" sx={priceDarkSx}>
                                        {tier.price}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={bestForDarkSx}>
                                        {tier.bestFor}
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap="16px" mb={5} flex={1}>
                                        {tier.features.map((f, i) => (
                                            <FeatureItem key={i} text={f} dark />
                                        ))}
                                    </Box>

                                    <Button fullWidth sx={orangeBtnSx} onClick={openForm} disableElevation>
                                        {tier.buttonText}
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{...lightCardSx, width: '100%'}}>
                                    <Typography className="font-headers" sx={tierLabelLightSx}>
                                        {tier.tier}
                                    </Typography>
                                    <Typography className="font-headers" sx={priceLightSx}>
                                        {tier.price}
                                    </Typography>
                                    <Typography className="font-regular-text" sx={bestForLightSx}>
                                        {tier.bestFor}
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap="16px" mb={5} flex={1}>
                                        {tier.features.map((f, i) => (
                                            <FeatureItem key={i} text={f} />
                                        ))}
                                    </Box>

                                    <Button fullWidth sx={lightBtnSx} onClick={openForm} disableElevation>
                                        {tier.buttonText}
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    ))}
                </Grid>

                {/* Trust strip */}
                <Box sx={trustStripSx}>
                    {trustItems.map((item, idx) => (
                        <Box key={idx} sx={{position: 'relative', pr: {md: idx < 3 ? 3 : 0}}}>
                            {/* Vertical divider — shown between items on md+ */}
                            {idx < 3 && (
                                <Box
                                    sx={{
                                        display: {xs: 'none', md: 'block'},
                                        position: 'absolute',
                                        right: 0,
                                        top: '20%',
                                        bottom: '20%',
                                        width: '1px',
                                        bgcolor: 'rgba(0,0,0,0.08)'
                                    }}
                                />
                            )}
                            <Box sx={trustItemContentSx}>
                                <Box sx={iconWrapSx}>
                                    <DisplayImage
                                        src={item.icon}
                                        alt={item.title}
                                        width={24}
                                        height={24}
                                        style={{width: '24px', height: '24px'}}
                                    />
                                </Box>
                                <Typography
                                    className="font-headers"
                                    sx={{fontWeight: fontWeight.semibold, fontSize: 15, color: '#0f1724', mb: '6px', lineHeight: '18px'}}
                                >
                                    {item.title}
                                </Typography>
                                <Typography
                                    className="font-regular-text"
                                    sx={{fontSize: 13, color: colors.text.muted, lineHeight: '18.2px'}}
                                >
                                    {item.body}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(WhiteLabelPricing);
